import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium ring-offset-background transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:scale-[0.98] disabled:pointer-events-none disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none disabled:bg-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary-strong text-primary-strong-foreground shadow-1 hover:brightness-[1.06] hover:shadow-3 hover:-translate-y-px",
        gradient:
          "bg-gradient-cta text-primary-foreground shadow-2 hover:brightness-[1.05] hover:shadow-3 hover:-translate-y-px",
        destructive:
          "bg-destructive text-destructive-foreground shadow-1 hover:brightness-[1.06] hover:shadow-3",
        outline:
          "border border-border bg-transparent text-foreground hover:bg-foreground/[0.04]",
        secondary:
          "bg-secondary text-secondary-foreground shadow-1 hover:brightness-[1.05]",
        tinted:
          "bg-[hsl(var(--primary)/0.12)] text-primary hover:bg-[hsl(var(--primary)/0.18)]",
        ghost: "text-foreground hover:bg-foreground/[0.04]",
        plain: "text-primary hover:bg-foreground/[0.04]",
        link: "text-primary underline-offset-4 hover:underline rounded-none",
      },
      size: {
        default: "h-10 px-5",
        sm: "h-9 px-4 text-[13px]",
        lg: "h-12 px-7 text-[15px]",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
